<template>
  <div :class="$style.commentSection">
    <div :class="$style.commentHeader">
      <h3 :class="$style.commentTitle">{{ commentLabel }} ({{ comments.length }})</h3>
    </div>

    <!-- 댓글 작성 (로그인 사용자만, 차단된 유저 제외) -->
    <div v-if="userStore.isLogin && !userStore.isBlocked" :class="$style.commentWrite">
      <div :class="$style.commentInput">
        <textarea v-model="newComment" :class="$style.commentTextarea" :placeholder="`${commentLabel}을 작성해주세요...`"
          rows="3"></textarea>
      </div>

      <!-- 이미지 미리보기 -->
      <div v-if="commentImagePreview" :class="$style.imagePreviewContainer">
        <div :class="$style.imagePreview">
          <img :src="commentImagePreview" alt="미리보기" />
          <button :class="$style.removeImageButton" @click="removeCommentImage">
            ×
          </button>
        </div>
      </div>

      <div :class="$style.commentActions">
        <label :class="$style.imageUploadLabel">
          <input type="file" accept="image/*" @change="handleCommentImageUpload" :class="$style.imageUploadInput" />
          📷 이미지
        </label>
        <button :class="$style.commentSubmit" @click="submitComment" :disabled="!newComment.trim() || commentLoading">
          {{ commentLoading ? "등록 중..." : `${commentLabel} 등록` }}
        </button>
      </div>
    </div>

    <!-- 댓글 목록 -->
    <div :class="$style.commentList">
      <div v-if="commentsLoading" :class="$style.commentLoading">
        <div :class="$style.spinner"></div>
        <p>{{ commentLabel }}을 불러오는 중...</p>
      </div>
      <div v-else-if="comments.length === 0" :class="$style.noComments">
        <p v-if="userStore.isLogin">아직 {{ commentLabel }}이 없습니다. 첫 번째 {{ commentLabel }}을 작성해보세요!</p>
        <p v-else>로그인 사용자만 이용이 가능합니다!</p>
      </div>
      <div v-else>
        <div v-for="comment in comments" :key="comment.id" :class="$style.commentItem">
          <!-- 댓글 -->
          <div :id="`comment-${comment.id}`" :class="$style.comment">
            <div :class="$style.commentInfo">
              <div :class="$style.writerInfo">
                <span :class="$style.commentWriter">{{ comment.writer }}</span>
                <UserLevelMini :level="comment.writerLevel || 1" />
              </div>
              <span :class="$style.commentDate">{{
                formatDate(comment.registeredAt)
              }}</span>
            </div>
            <div :class="$style.commentContent">
              {{ comment.contents }}
            </div>
            <!-- 댓글 이미지 -->
            <div v-if="comment.imageUrl" :class="$style.commentImageContainer">
              <img :src="comment.imageUrl" :class="$style.commentImage" alt="댓글 이미지"
                @click="openImageModal(comment.imageUrl)" />
            </div>
            <div :class="$style.commentActions">
              <button :class="[
                $style.likeButton,
                { [$style.liked]: comment.isLiked },
              ]" @click="toggleCommentLike(comment.id)" :disabled="!userStore.isLogin">
                ❤️ {{ comment.like || 0 }}
              </button>
              <button v-if="!userStore.isBlocked" :class="$style.replyButton" @click="toggleReply(comment.id)">
                답글 ({{ comment.recomments ? comment.recomments.length : 0 }})
              </button>
              <button v-if="!userStore.isBlocked" :class="$style.reportButton"
                @click="showReportModal('comment', comment.id)">
                🚨
              </button>
              <button v-if="canDeleteComment(comment)" :class="$style.deleteButton"
                @click="showDeleteModal('comment', comment.id)">
                🗑️
              </button>
            </div>
          </div>

          <!-- 대댓글 작성 (답글 버튼 클릭 시, 로그인 사용자만, 차단된 유저 제외) -->
          <div v-if="userStore.isLogin && !userStore.isBlocked && showReplyInput === comment.id"
            :class="$style.recommentWrite">
            <div :class="$style.recommentInput">
              <textarea v-model="newRecomment[comment.id]" :class="$style.recommentTextarea" placeholder="답글을 작성해주세요..."
                rows="2"></textarea>
            </div>
            <div :class="$style.recommentActions">
              <button :class="$style.recommentSubmit" @click="submitRecomment(comment.id)" :disabled="!newRecomment[comment.id]?.trim() ||
                recommentLoading[comment.id]
                ">
                {{ recommentLoading[comment.id] ? "등록 중..." : "답글 등록" }}
              </button>
              <button :class="$style.recommentCancel" @click="cancelReply(comment.id)">
                취소
              </button>
            </div>
          </div>

          <!-- 대댓글 목록 -->
          <div v-if="comment.recomments && comment.recomments.length > 0" :class="$style.recommentList">
            <div v-for="recomment in comment.recomments" :key="recomment.id" :class="$style.recommentItem">
              <div :class="$style.recommentInfo">
                <div :class="$style.writerInfo">
                  <span :class="$style.recommentWriter">{{
                    recomment.writer
                  }}</span>
                  <UserLevelMini :level="recomment.writerLevel || 1" />
                </div>
                <span :class="$style.recommentDate">{{
                  formatDate(recomment.registeredAt)
                }}</span>
                <span v-if="recomment.replyTo" :class="$style.replyTo">
                  → {{ recomment.replyTo }}님에게 답글
                </span>
              </div>
              <div :class="$style.recommentContent">
                {{ recomment.contents }}
              </div>
              <div :class="$style.recommentActions">
                <button :class="[
                  $style.likeButton,
                  { [$style.liked]: recomment.isLiked },
                ]" @click="toggleRecommentLike(comment.id, recomment.id)" :disabled="!userStore.isLogin">
                  ❤️ {{ recomment.like || 0 }}
                </button>
                <button v-if="!userStore.isBlocked" :class="$style.replyButton"
                  @click="toggleRecommentReply(comment.id, recomment.id)">
                  답글
                </button>
                <button v-if="!userStore.isBlocked" :class="$style.reportButton" @click="
                  showReportModal('recomment', recomment.id, comment.id)
                  ">
                  🚨
                </button>
                <button v-if="canDeleteRecomment(recomment)" :class="$style.deleteButton" @click="
                  showDeleteModal('recomment', recomment.id, comment.id)
                  ">
                  🗑️
                </button>
              </div>

              <!-- 대댓글의 답글 작성 (로그인 사용자만) -->
              <div v-if="
                userStore.isLogin &&
                showRecommentReplyInput === `${comment.id}-${recomment.id}`
              " :class="$style.recommentReplyWrite">
                <div :class="$style.recommentReplyInput">
                  <textarea v-model="newRecommentReply[`${comment.id}-${recomment.id}`]"
                    :class="$style.recommentReplyTextarea" :placeholder="`${recomment.writer}님에게 답글을 작성해주세요...`"
                    rows="2"></textarea>
                </div>
                <div :class="$style.recommentReplyActions">
                  <button :class="$style.recommentReplySubmit" @click="
                    submitRecommentReply(
                      comment.id,
                      recomment.id,
                      recomment.writer
                    )
                    " :disabled="!newRecommentReply[
                      `${comment.id}-${recomment.id}`
                    ]?.trim() ||
                      recommentReplyLoading[`${comment.id}-${recomment.id}`]
                      ">
                    {{
                      recommentReplyLoading[`${comment.id}-${recomment.id}`]
                        ? "등록 중..."
                        : "답글 등록"
                    }}
                  </button>
                  <button :class="$style.recommentReplyCancel" @click="cancelRecommentReply(comment.id, recomment.id)">
                    취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 신고 모달 -->
    <div v-if="showReportModalFlag" :class="$style.reportModal">
      <div :class="$style.reportModalContent">
        <div :class="$style.reportModalHeader">
          <h3>신고하기</h3>
          <button @click="closeReportModal" :class="$style.closeButton">
            ×
          </button>
        </div>
        <div :class="$style.reportModalBody">
          <p>신고 사유를 선택해주세요:</p>
          <div :class="$style.reportReasons">
            <label v-for="reason in reportReasons" :key="reason.value" :class="$style.reportReason">
              <input type="radio" v-model="selectedReportReason" :value="reason.value" :class="$style.reportRadio" />
              <span>{{ reason.label }}</span>
            </label>
          </div>
          <div :class="$style.reportTextarea">
            <textarea v-model="reportDescription" placeholder="추가 설명이 있다면 작성해주세요 (선택사항)" rows="3"></textarea>
          </div>
        </div>
        <div :class="$style.reportModalFooter">
          <button @click="closeReportModal" :class="$style.cancelButton">
            취소
          </button>
          <button @click="submitReport" :class="$style.submitButton" :disabled="!selectedReportReason || reportLoading">
            {{ reportLoading ? "신고 중..." : "신고하기" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 삭제 확인 모달 -->
    <div v-if="showDeleteModalFlag" :class="$style.deleteModal">
      <div :class="$style.deleteModalContent">
        <div :class="$style.deleteModalHeader">
          <h3>{{ deleteTargetType === "comment" ? commentLabel : "답글" }} 삭제</h3>
          <button @click="closeDeleteModal" :class="$style.closeButton">
            ×
          </button>
        </div>
        <div :class="$style.deleteModalBody">
          <p>
            정말로 이 {{ deleteTargetType === "comment" ? commentLabel : "답글" }}을
            삭제하시겠습니까?
          </p>
          <p :class="$style.deleteWarning">
            삭제된 {{ deleteTargetType === "comment" ? commentLabel : "답글" }}은
            복구할 수 없습니다.
          </p>
        </div>
        <div :class="$style.deleteModalFooter">
          <button @click="closeDeleteModal" :class="$style.cancelButton">
            취소
          </button>
          <button @click="confirmDelete" :class="$style.deleteConfirmButton" :disabled="deleteLoading">
            {{ deleteLoading ? "삭제 중..." : "삭제하기" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 이미지 확대 모달 -->
    <div v-if="showImageModal" :class="$style.imageModal" @click="closeImageModal">
      <div :class="$style.imageModalContent" @click.stop>
        <button @click="closeImageModal" :class="$style.closeImageButton">
          ×
        </button>
        <img :src="selectedImageUrl" :class="$style.modalImage" alt="확대 이미지" />
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query, } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "/src/store/user.js";
import { getUserLevel } from "@/utils/levelSystem";
import UserLevelMini from "@/components/UserLevelMini.vue";
import { createNotification } from "@/utils/write/alarm";
import {
  createComment,
  createRecomment,
  deleteComment as deleteCommentApi,
  deleteRecomment as deleteRecommentApi,
  toggleCommentLike as toggleCommentLikeApi,
  toggleRecommentLike as toggleRecommentLikeApi,
} from "@/utils/write/comment";
import { createReport } from "@/utils/write/report";

export default {
  name: "CommentSection",
  components: {
    UserLevelMini,
  },
  props: {
    boardId: {
      type: String,
      required: true,
    },
    postWriterEmail: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      comments: [],
      commentsLoading: false,
      newComment: "",
      commentLoading: false,
      commentImage: null, // 댓글 이미지 파일
      commentImagePreview: null, // 댓글 이미지 미리보기 URL
      showReplyInput: null,
      newRecomment: {},
      recommentLoading: {},
      showRecommentReplyInput: null,
      newRecommentReply: {},
      recommentReplyLoading: {},
      showReportModalFlag: false,
      reportTarget: null,
      reportTargetType: null,
      reportTargetCommentId: null,
      selectedReportReason: "",
      reportDescription: "",
      reportLoading: false,
      reportReasons: [
        { value: "spam", label: "스팸/광고" },
        { value: "inappropriate", label: "부적절한 내용" },
        { value: "harassment", label: "괴롭힘/욕설" },
        { value: "fake", label: "허위 정보" },
        { value: "copyright", label: "저작권 침해" },
        { value: "other", label: "기타" },
      ],
      showDeleteModalFlag: false,
      deleteTargetType: null,
      deleteTarget: null,
      deleteTargetCommentId: null,
      deleteLoading: false,
      userStore: useUserStore(),
      showImageModal: false, // 댓글 이미지 확대 모달
      selectedImageUrl: "", // 선택된 이미지 URL
    };
  },
  computed: {
    commentLabel() {
      return "댓글";
    },
  },
  async mounted() {
    await this.loadComments();
  },
  methods: {
    async loadComments() {
      try {
        this.commentsLoading = true;
        const commentsRef = collection(
          firestore,
          "boards",
          this.boardId,
          "comments"
        );
        const q = query(commentsRef, orderBy("registeredAt", "asc"));
        const commentsSnapshot = await getDocs(q);

        this.comments = [];
        // 삭제되지 않은 댓글만 필터링
        const filteredComments = commentsSnapshot.docs.filter((doc) => {
          const data = doc.data();
          return data.deleted !== true;
        });

        for (const commentDoc of filteredComments) {
          const commentData = commentDoc.data();

          // 대댓글 로드
          const recommentsRef = collection(
            firestore,
            "boards",
            this.boardId,
            "comments",
            commentDoc.id,
            "recomments"
          );
          const recommentQuery = query(
            recommentsRef,
            orderBy("registeredAt", "asc")
          );
          const recommentsSnapshot = await getDocs(recommentQuery);

          // 삭제되지 않은 답글만 필터링
          const filteredRecomments = recommentsSnapshot.docs.filter((doc) => {
            const data = doc.data();
            return data.deleted !== true;
          });

          const recomments = await Promise.all(
            filteredRecomments.map(async (recommentDoc) => {
              const recommentData = recommentDoc.data();
              const recomment = {
                id: recommentDoc.id,
                ...recommentData,
                isLiked:
                  recommentData.likedUsers &&
                  recommentData.likedUsers.includes(this.userStore.email),
              };

              // 답글 작성자 레벨 정보 가져오기
              try {
                if (recommentData.writerEmail) {
                  const levelInfo = await getUserLevel(
                    recommentData.writerEmail
                  );
                  recomment.writerLevel = levelInfo.level;
                }
              } catch (error) {
                console.warn("답글 작성자 레벨 정보 가져오기 실패:", error);
              }

              return recomment;
            })
          );

          const comment = {
            id: commentDoc.id,
            ...commentData,
            recomments,
            isLiked:
              commentData.likedUsers &&
              commentData.likedUsers.includes(this.userStore.email),
          };

          // 댓글 작성자 레벨 정보 가져오기
          try {
            if (commentData.writerEmail) {
              const levelInfo = await getUserLevel(commentData.writerEmail);
              comment.writerLevel = levelInfo.level;
            } else {
              console.warn("댓글에 writerEmail이 없습니다:", commentData);
            }
          } catch (error) {
            console.warn("댓글 작성자 레벨 정보 가져오기 실패:", error);
          }

          this.comments.push(comment);
        }
      } catch (error) {
        console.error("댓글 로드 오류:", error);
      } finally {
        this.commentsLoading = false;
      }
    },

    // 댓글 이미지 업로드 처리
    handleCommentImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 이미지 파일 체크
      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드할 수 있습니다.");
        event.target.value = "";
        return;
      }

      this.commentImage = file;

      // 미리보기 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        this.commentImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // 댓글 이미지 제거
    removeCommentImage() {
      this.commentImage = null;
      this.commentImagePreview = null;
    },

    async submitComment() {
      if (!this.newComment.trim() || !this.userStore.isLogin) {
        if (!this.userStore.isLogin) {
          this.$alert("로그인이 필요합니다.");
        }
        return;
      }

      try {
        this.commentLoading = true;

        const commentData = {
          contents: this.newComment.trim(),
          writer: this.userStore.nickname,
          writerEmail: this.userStore.email,
        };

        // boards API 사용
        await createComment(this.boardId, commentData, this.commentImage);

        this.newComment = "";
        this.removeCommentImage();

        // 게시글 작성자에게 알림 생성
        await this.createCommentNotification();

        await this.loadComments();
      } catch (error) {
        console.error("댓글 등록 오류:", error);
        this.$alert(`${this.commentLabel} 등록 중 오류가 발생했습니다.`);
      } finally {
        this.commentLoading = false;
      }
    },

    async submitRecomment(commentId) {
      const recommentText = this.newRecomment[commentId];
      if (!recommentText?.trim() || !this.userStore.isLogin) {
        if (!this.userStore.isLogin) {
          this.$alert("로그인이 필요합니다.");
        }
        return;
      }

      try {
        this.recommentLoading[commentId] = true;

        const recommentData = {
          contents: recommentText.trim(),
          writer: this.userStore.nickname,
          writerEmail: this.userStore.email,
          replyTo: null,
        };

        // boards API 사용
        await createRecomment(this.boardId, commentId, recommentData);

        this.newRecomment[commentId] = "";
        this.showReplyInput = null;

        // 원댓글 작성자에게 알림 생성
        const comment = this.comments.find((c) => c.id === commentId);
        if (comment && comment.writerEmail) {
          await this.createRecommentNotification(comment.writerEmail);
        }

        await this.loadComments();
      } catch (error) {
        console.error("답글 등록 오류:", error);
        this.$alert("답글 등록 중 오류가 발생했습니다.");
      } finally {
        this.recommentLoading[commentId] = false;
      }
    },

    async submitRecommentReply(commentId, recommentId, replyToWriter) {
      const replyKey = `${commentId}-${recommentId}`;
      const replyText = this.newRecommentReply[replyKey];
      if (!replyText?.trim() || !this.userStore.isLogin) {
        if (!this.userStore.isLogin) {
          this.$alert("로그인이 필요합니다.");
        }
        return;
      }

      try {
        this.recommentReplyLoading[replyKey] = true;

        const recommentData = {
          contents: replyText.trim(),
          writer: this.userStore.nickname,
          writerEmail: this.userStore.email,
          replyTo: replyToWriter,
        };

        // boards API 사용
        await createRecomment(this.boardId, commentId, recommentData);

        this.newRecommentReply[replyKey] = "";
        this.showRecommentReplyInput = null;

        await this.loadComments();
      } catch (error) {
        console.error("답글 등록 오류:", error);
        this.$alert("답글 등록 중 오류가 발생했습니다.");
      } finally {
        this.recommentReplyLoading[replyKey] = false;
      }
    },

    async toggleCommentLike(commentId) {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }

      try {
        const comment = this.comments.find((c) => c.id === commentId);
        if (!comment) return;

        const isCurrentlyLiked = comment.isLiked;

        // boards API 사용
        const newLikedState = await toggleCommentLikeApi(
          this.boardId,
          commentId,
          this.userStore.email,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        comment.like = (comment.like || 0) + (newLikedState ? 1 : -1);
        comment.isLiked = newLikedState;
      } catch (error) {
        console.error("댓글 좋아요 오류:", error);
        this.$alert("좋아요 처리 중 오류가 발생했습니다.");
      }
    },

    async toggleRecommentLike(commentId, recommentId) {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }

      try {
        const comment = this.comments.find((c) => c.id === commentId);
        const recomment = comment?.recomments?.find(
          (r) => r.id === recommentId
        );

        if (!recomment) return;

        const isCurrentlyLiked = recomment.isLiked;

        // boards API 사용
        const newLikedState = await toggleRecommentLikeApi(
          this.boardId,
          commentId,
          recommentId,
          this.userStore.email,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        recomment.like = (recomment.like || 0) + (newLikedState ? 1 : -1);
        recomment.isLiked = newLikedState;
      } catch (error) {
        console.error("대댓글 좋아요 오류:", error);
        this.$alert("좋아요 처리 중 오류가 발생했습니다.");
      }
    },

    toggleReply(commentId) {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }
      if (this.showReplyInput === commentId) {
        this.showReplyInput = null;
        this.newRecomment[commentId] = "";
      } else {
        this.showReplyInput = commentId;
        this.newRecomment[commentId] = "";
      }
    },

    toggleRecommentReply(commentId, recommentId) {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }
      const replyKey = `${commentId}-${recommentId}`;
      if (this.showRecommentReplyInput === replyKey) {
        this.showRecommentReplyInput = null;
        this.newRecommentReply[replyKey] = "";
      } else {
        this.showRecommentReplyInput = replyKey;
        this.newRecommentReply[replyKey] = "";
      }
    },

    cancelReply(commentId) {
      this.showReplyInput = null;
      this.newRecomment[commentId] = "";
    },

    cancelRecommentReply(commentId, recommentId) {
      const replyKey = `${commentId}-${recommentId}`;
      this.showRecommentReplyInput = null;
      this.newRecommentReply[replyKey] = "";
    },

    showReportModal(type, targetId, commentId = null) {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }

      this.reportTargetType = type;
      this.reportTarget = targetId;
      this.reportTargetCommentId = commentId;
      this.showReportModalFlag = true;
      this.selectedReportReason = "";
      this.reportDescription = "";
    },

    closeReportModal() {
      this.showReportModalFlag = false;
      this.reportTargetType = null;
      this.reportTarget = null;
      this.reportTargetCommentId = null;
      this.selectedReportReason = "";
      this.reportDescription = "";
    },

    async submitReport() {
      if (!this.selectedReportReason) {
        this.$alert("신고 사유를 선택해주세요.");
        return;
      }

      try {
        this.reportLoading = true;

        // 문서 위치 정보 생성
        let documentPath = "";
        if (this.reportTargetType === "post") {
          documentPath = `boards/${this.boardId}`;
        } else if (this.reportTargetType === "comment") {
          documentPath = `boards/${this.boardId}/comments/${this.reportTarget}`;
        } else if (this.reportTargetType === "recomment") {
          documentPath = `boards/${this.boardId}/comments/${this.reportTargetCommentId}/recomments/${this.reportTarget}`;
        }

        const reportData = {
          reporter: this.userStore.email,
          reporterNickname: this.userStore.nickname,
          documentPath: documentPath,
          documentType: this.reportTargetType,
          reason: this.selectedReportReason,
          description: this.reportDescription.trim(),
        };

        await createReport(reportData);

        this.$alert("신고가 접수되었습니다.");
        this.closeReportModal();
      } catch (error) {
        console.error("신고 제출 오류:", error);
        this.$alert("신고 제출 중 오류가 발생했습니다.");
      } finally {
        this.reportLoading = false;
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return "날짜 정보 없음";

      try {
        const date = timestamp.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
        return date.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return "날짜 정보 없음";
      }
    },

    // 댓글 삭제 권한 확인
    canDeleteComment(comment) {
      if (!this.userStore.isLogin) return false;
      // 관리자이거나 본인이 작성한 댓글인 경우
      return (
        this.userStore.auth === "admin" ||
        comment.writerEmail === this.userStore.email
      );
    },

    // 대댓글 삭제 권한 확인
    canDeleteRecomment(recomment) {
      if (!this.userStore.isLogin) return false;
      // 관리자이거나 본인이 작성한 대댓글인 경우
      return (
        this.userStore.auth === "admin" ||
        recomment.writerEmail === this.userStore.email
      );
    },

    // 삭제 모달 표시
    showDeleteModal(type, targetId, commentId = null) {
      this.deleteTargetType = type;
      this.deleteTarget = targetId;
      this.deleteTargetCommentId = commentId;
      this.showDeleteModalFlag = true;
    },

    // 삭제 모달 닫기
    closeDeleteModal() {
      this.showDeleteModalFlag = false;
      this.deleteTargetType = null;
      this.deleteTarget = null;
      this.deleteTargetCommentId = null;
    },

    // 삭제 확인
    async confirmDelete() {
      if (this.deleteTargetType === "comment") {
        await this.deleteComment(this.deleteTarget);
      } else if (this.deleteTargetType === "recomment") {
        await this.deleteRecomment(
          this.deleteTargetCommentId,
          this.deleteTarget
        );
      }
      this.closeDeleteModal();
    },

    // 댓글 삭제
    async deleteComment(commentId) {
      try {
        this.deleteLoading = true;

        // boards API 사용
        await deleteCommentApi(this.boardId, commentId);

        this.$alert(`${this.commentLabel}이 삭제되었습니다.`);
        await this.loadComments();
      } catch (error) {
        console.error("댓글 삭제 오류:", error);
        this.$alert(`${this.commentLabel} 삭제 중 오류가 발생했습니다.`);
      } finally {
        this.deleteLoading = false;
      }
    },

    // 대댓글 삭제
    async deleteRecomment(commentId, recommentId) {
      try {
        this.deleteLoading = true;

        // boards API 사용
        await deleteRecommentApi(this.boardId, commentId, recommentId);

        this.$alert("답글이 삭제되었습니다.");
        await this.loadComments();
      } catch (error) {
        console.error("답글 삭제 오류:", error);
        this.$alert("답글 삭제 중 오류가 발생했습니다.");
      } finally {
        this.deleteLoading = false;
      }
    },

    // 이미지 모달 열기
    openImageModal(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.showImageModal = true;
    },

    // 이미지 모달 닫기
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImageUrl = "";
    },

    // 댓글 알림 생성
    async createCommentNotification() {
      try {
        // 게시글 작성자가 자신이면 알림 생성하지 않음
        if (this.postWriterEmail === this.userStore.email) {
          return;
        }

        if (!this.postWriterEmail) {
          return;
        }

        await createNotification({
          userId: this.postWriterEmail,
          type: "comment",
          title: `${this.commentLabel}이 달렸습니다`,
          message: `${this.userStore.nickname}님이 ${this.commentLabel}을 남겼습니다.`,
          boardId: this.boardId,
          read: false,
        });
      } catch (error) {
        console.error("댓글 알림 생성 오류:", error);
      }
    },

    // 답글 알림 생성
    async createRecommentNotification(recommentWriterEmail) {
      try {
        // 답글 작성자가 자신이면 알림 생성하지 않음
        if (recommentWriterEmail === this.userStore.email) {
          return;
        }

        if (!recommentWriterEmail) {
          return;
        }

        await createNotification({
          userId: recommentWriterEmail,
          type: "comment",
          title: "답글이 달렸습니다",
          message: `${this.userStore.nickname}님이 답글을 남겼습니다.`,
          boardId: this.boardId,
          read: false,
        });
      } catch (error) {
        console.error("답글 알림 생성 오류:", error);
      }
    },
  },
};
</script>

<style module>
.commentSection {
  flex: 1;
  padding: 30px;
}

.commentHeader {
  margin-bottom: 20px;
}

.commentTitle {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

/* 댓글 작성 */
.commentWrite {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.commentInput {
  margin-bottom: 15px;
}

.commentTextarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
  min-height: 80px;
}

.commentTextarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.commentActions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.imageUploadLabel {
  background: #f8f9fa;
  color: #333;
  border: 1px solid #ddd;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  display: inline-block;
}

.imageUploadLabel:hover {
  background: #e9ecef;
  border-color: #66bdff;
}

.imageUploadInput {
  display: none;
}

.imagePreviewContainer {
  margin: 10px 0;
}

.imagePreview {
  position: relative;
  display: inline-block;
  max-width: 120px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e9ecef;
}

.imagePreview img {
  width: 100%;
  height: auto;
  display: block;
}

.removeImageButton {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(255, 107, 107, 0.9);
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
  transition: all 0.2s ease;
}

.removeImageButton:hover {
  background: rgba(255, 82, 82, 1);
  transform: scale(1.1);
}

.commentSubmit {
  background: #66bdff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.commentSubmit:hover:not(:disabled) {
  background: #4a9eff;
}

.commentSubmit:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 댓글 목록 */
.commentList {
  margin-top: 20px;
}

.commentLoading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.noComments {
  text-align: center;
  padding: 40px;
  color: #999;
  font-style: italic;
}

.commentItem {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.commentItem:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* 댓글 */
.comment {
  margin-bottom: 15px;
}

.commentInfo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}

.writerInfo {
  display: flex;
  align-items: center;
  gap: 4px;
}

.commentWriter {
  font-weight: 500;
  color: #333;
}

.commentDate {
  color: #999;
}

.commentContent {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 10px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "NanumSquareRoundR", sans-serif;
}

.commentImageContainer {
  margin: 10px 0;
}

.commentImage {
  max-width: 150px;
  max-height: 150px;
  width: auto;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}

.commentImage:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.commentActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.likeButton {
  background: #ff6b9d;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.likeButton:hover {
  background: #ff5582;
  transform: translateY(-1px);
}

.likeButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.likeButton.liked {
  background: #ff1744;
}

.likeButton.liked:hover {
  background: #d50000;
}

.replyButton {
  background: none;
  border: none;
  color: #66bdff;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.replyButton:hover {
  background: #e3f2fd;
}

.reportButton {
  background: none;
  border: none;
  color: #ff6b6b;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.reportButton:hover {
  background: #ffe6e6;
}

.deleteButton {
  background: none;
  border: none;
  color: #ff6b6b;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteButton:hover {
  background: #ffe6e6;
}

/* 대댓글 작성 */
.recommentWrite {
  margin: 15px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #66bdff;
}

.recommentInput {
  margin-bottom: 10px;
}

.recommentTextarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
  min-height: 60px;
}

.recommentTextarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.recommentActions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.recommentSubmit {
  background: #66bdff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.recommentSubmit:hover:not(:disabled) {
  background: #4a9eff;
}

.recommentSubmit:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.recommentCancel {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.recommentCancel:hover {
  background: #5a6268;
}

/* 대댓글 목록 */
.recommentList {
  margin-top: 15px;
  padding-left: 20px;
  border-left: 2px solid #e9ecef;
}

.recommentItem {
  margin-bottom: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.recommentItem:last-child {
  margin-bottom: 0;
}

.recommentInfo {
  display: flex;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 12px;
  flex-wrap: wrap;
}

.recommentWriter {
  font-weight: 500;
  color: #333;
}

.recommentDate {
  color: #999;
}

.replyTo {
  color: #66bdff;
  font-weight: 500;
  font-size: 11px;
}

.recommentContent {
  font-size: 13px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "NanumSquareRoundR", sans-serif;
}

.recommentActions {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 대댓글의 답글 작성 */
.recommentReplyWrite {
  margin: 10px 0;
  padding: 12px;
  background: #f0f8ff;
  border-radius: 4px;
  border-left: 3px solid #4a9eff;
}

.recommentReplyInput {
  margin-bottom: 8px;
}

.recommentReplyTextarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
  min-height: 50px;
}

.recommentReplyTextarea:focus {
  outline: none;
  border-color: #4a9eff;
  box-shadow: 0 0 0 2px rgba(74, 158, 255, 0.2);
}

.recommentReplyActions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.recommentReplySubmit {
  background: #4a9eff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.recommentReplySubmit:hover:not(:disabled) {
  background: #3a8eef;
}

.recommentReplySubmit:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.recommentReplyCancel {
  background: #6c757d;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.recommentReplyCancel:hover {
  background: #5a6268;
}

/* 신고 모달 */
.reportModal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 830;
}

.reportModalContent {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.reportModalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.reportModalHeader h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeButton:hover {
  color: #333;
}

.reportModalBody {
  padding: 20px;
}

.reportModalBody p {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #666;
}

.reportReasons {
  margin-bottom: 20px;
}

.reportReason {
  display: block;
  margin-bottom: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.reportRadio {
  margin-right: 8px;
}

.reportTextarea {
  margin-top: 15px;
}

.reportTextarea textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: vertical;
}

.reportTextarea textarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.reportModalFooter {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #e9ecef;
}

.cancelButton {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.cancelButton:hover {
  background: #5a6268;
}

.submitButton {
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.submitButton:hover:not(:disabled) {
  background: #c82333;
}

.submitButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 삭제 모달 */
.deleteModal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 840;
}

.deleteModalContent {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
}

.deleteModalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.deleteModalHeader h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.deleteModalBody {
  padding: 20px;
}

.deleteModalBody p {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.deleteWarning {
  color: #dc3545 !important;
  font-size: 13px !important;
  margin-top: 10px !important;
}

.deleteModalFooter {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #e9ecef;
}

.deleteConfirmButton {
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteConfirmButton:hover:not(:disabled) {
  background: #c82333;
}

.deleteConfirmButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* ========================================
   반응형 디자인
   - Large: 1025px ~ (기본 스타일)
   - Medium: 481px ~ 1024px
   - Small: ~ 480px
   ======================================== */

/* Medium (481px ~ 1024px) */
@media (max-width: 1024px) {
  .commentSection {
    padding: 20px;
  }

  .commentTitle {
    font-size: 18px;
  }

  .commentWrite {
    padding: 15px;
  }

  .commentTextarea {
    min-height: 70px;
  }

  .commentImage {
    max-width: 120px;
    max-height: 120px;
  }

  .imagePreview {
    max-width: 100px;
  }

  .recommentWrite {
    padding: 12px;
  }

  .recommentList {
    padding-left: 15px;
  }

  .recommentTextarea {
    min-height: 55px;
  }

  .recommentReplyWrite {
    padding: 10px;
  }

  .recommentReplyTextarea {
    min-height: 45px;
  }

  .reportModalContent {
    width: 95%;
    margin: 20px;
  }

  .reportModalHeader,
  .reportModalBody,
  .reportModalFooter {
    padding: 15px;
  }

  .deleteModalContent {
    width: 95%;
    margin: 20px;
  }

  .deleteModalHeader,
  .deleteModalBody,
  .deleteModalFooter {
    padding: 15px;
  }
}

/* Small (~ 480px) */
@media (max-width: 480px) {
  .commentSection {
    padding: 15px;
  }

  .commentHeader {
    margin-bottom: 15px;
  }

  .commentTitle {
    font-size: 16px;
  }

  .commentWrite {
    margin-bottom: 20px;
    padding: 12px;
  }

  .commentInput {
    margin-bottom: 10px;
  }

  .commentTextarea {
    padding: 10px;
    font-size: 13px;
    min-height: 60px;
  }

  .commentActions {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .imageUploadLabel {
    padding: 8px 16px;
    font-size: 13px;
    text-align: center;
  }

  .commentSubmit {
    padding: 10px 16px;
    font-size: 13px;
  }

  .commentImage {
    max-width: 100px;
    max-height: 100px;
  }

  .imagePreview {
    max-width: 80px;
  }

  .removeImageButton {
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .commentItem {
    margin-bottom: 15px;
    padding-bottom: 15px;
  }

  .commentInfo {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 12px;
  }

  .commentContent {
    font-size: 13px;
  }

  .commentActions {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
  }

  .likeButton,
  .replyButton,
  .reportButton,
  .deleteButton {
    font-size: 11px;
    padding: 4px 6px;
  }

  .recommentWrite {
    margin: 10px 0;
    padding: 10px;
  }

  .recommentInput {
    margin-bottom: 8px;
  }

  .recommentTextarea {
    padding: 8px;
    font-size: 12px;
    min-height: 50px;
  }

  .recommentActions {
    gap: 6px;
  }

  .recommentSubmit,
  .recommentCancel {
    padding: 6px 12px;
    font-size: 11px;
  }

  .recommentList {
    margin-top: 10px;
    padding-left: 10px;
  }

  .recommentItem {
    margin-bottom: 10px;
    padding: 8px;
  }

  .recommentInfo {
    gap: 6px;
    font-size: 11px;
  }

  .recommentContent {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .recommentActions {
    gap: 6px;
  }

  .recommentReplyWrite {
    margin: 8px 0;
    padding: 8px;
  }

  .recommentReplyInput {
    margin-bottom: 6px;
  }

  .recommentReplyTextarea {
    padding: 6px;
    font-size: 11px;
    min-height: 40px;
  }

  .recommentReplyActions {
    gap: 4px;
  }

  .recommentReplySubmit,
  .recommentReplyCancel {
    padding: 5px 10px;
    font-size: 10px;
  }

  .reportModalContent {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .reportModalHeader {
    padding: 15px 12px;
  }

  .reportModalHeader h3 {
    font-size: 16px;
  }

  .closeButton {
    font-size: 20px;
    width: 24px;
    height: 24px;
  }

  .reportModalBody {
    padding: 12px;
  }

  .reportModalBody p {
    font-size: 13px;
  }

  .reportReason {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .reportTextarea textarea {
    padding: 8px;
    font-size: 13px;
  }

  .reportModalFooter {
    padding: 12px;
    gap: 8px;
  }

  .cancelButton,
  .submitButton {
    padding: 8px 16px;
    font-size: 13px;
  }

  .deleteModalContent {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .deleteModalHeader {
    padding: 15px 12px;
  }

  .deleteModalHeader h3 {
    font-size: 16px;
  }

  .deleteModalBody {
    padding: 12px;
  }

  .deleteModalBody p {
    font-size: 13px;
  }

  .deleteWarning {
    font-size: 12px !important;
  }

  .deleteModalFooter {
    padding: 12px;
    gap: 8px;
  }

  .deleteConfirmButton {
    padding: 8px 16px;
    font-size: 13px;
  }

  .imageModalContent {
    max-width: 95vw;
    max-height: 85vh;
  }

  .closeImageButton {
    top: -35px;
    font-size: 30px;
    width: 35px;
    height: 35px;
  }
}

/* 이미지 확대 모달 */
.imageModal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 850;
}

.imageModalContent {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modalImage {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.closeImageButton {
  position: absolute;
  top: -40px;
  right: 0;
  background: transparent;
  color: white;
  border: none;
  font-size: 36px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeImageButton:hover {
  color: #66bdff;
}
</style>
